import { AuthContextProvider } from "@elit-hair/elit-tools";
import { TooltipProvider } from "@elit-hair/elit-ui";
import { ErrorBoundary } from "@sentry/react";
import { RouterProvider } from "@tanstack/react-router";
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { Toaster } from "sonner";

import { ClockProvider } from "@/providers/clock-provider";
import { LanguageProvider } from "@/providers/general/language-provider";
import { UserStatusProvider } from "@/providers/general/user-status.provider";

import { TopbarProvider } from "@/providers/hooks/use-topbar";
import { handleAuthSvcLogout } from "@/utils/auth-token.utils";
import {
  isChunkLoadError,
  registerChunkLoadErrorRecovery,
} from "@/utils/chunk-load-error-recovery";

import { DevIndicator } from "./components/dev-indicator";
import { LoadingScreen } from "./components/loading-screen";
import { SettingsProvider } from "./providers/general/settings-provider";
import { ThemeProviderWrapper } from "./providers/general/theme-provider-wrapper";
import { router } from "./router";
import "./styles/globals.css";
import "./styles/reactQuill.css";
import { QueryClientProvider } from "./utils/query-client";
import "./utils/sentry";

registerChunkLoadErrorRecovery();

function AppErrorFallback({ error }: { error: unknown }) {
  if (isChunkLoadError(error)) {
    window.location.reload();
    return <LoadingScreen />;
  }

  return <div>Something went wrong. Please try again later.</div>;
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary fallback={({ error }) => <AppErrorFallback error={error} />}>
      <Suspense fallback={<LoadingScreen />}>
        <QueryClientProvider>
          <TopbarProvider>
            <AuthContextProvider beforeLogout={handleAuthSvcLogout}>
              <SettingsProvider>
                <ClockProvider>
                  <ThemeProviderWrapper>
                    <UserStatusProvider>
                      <LanguageProvider>
                        <TooltipProvider>
                          <RouterProvider router={router} />
                          <Toaster />
                          <DevIndicator />
                        </TooltipProvider>
                      </LanguageProvider>
                    </UserStatusProvider>
                  </ThemeProviderWrapper>
                </ClockProvider>
              </SettingsProvider>
            </AuthContextProvider>
          </TopbarProvider>
        </QueryClientProvider>
      </Suspense>
    </ErrorBoundary>
  </React.StrictMode>,
);
